<div class="container mx-auto px-4 py-8">
  <!-- AI News Section -->
  <section>
    <div class="mb-12 text-center">
      <h2 class="mb-3 text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100 md:text-4xl">
        Latest AI News
      </h2>
      <p class="mx-auto max-w-2xl text-gray-600 dark:text-gray-300">
        Stay updated with the latest developments in artificial intelligence, machine learning, and related
        technologies.
      </p>
    </div>

    @if (loading) {
      <div class="flex justify-center items-center h-64">
        <app-loading-spinner message="Loading articles..." />
      </div>
    } @else if (error) {
      <app-error-message title="Error" [message]="error" [showRetryButton]="true" (retry)="loadArticles()" />
    } @else if (articles.length === 0) {
      <div
        role="status"
        class="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center dark:border-gray-700 dark:bg-gray-800/50"
      >
        <ng-icon hlm [size]="'xl'" [name]="'lucideFileText'" class="text-gray-400 mb-4" />
        <h2 class="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">No Articles Found</h2>
        <p class="text-gray-600 dark:text-gray-300">There are currently no news articles available.</p>
      </div>
    } @else {
      <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        @for (article of articles; track article.id) {
          <app-news-card [article]="article" />
        }
      </div>

      <div class="mt-12 text-center">
        <a hlmBtn routerLink="/news" class="inline-flex items-center">
          <span>All News</span>
          <ng-icon hlm [size]="'sm'" [name]="'lucideArrowRight'" class="ml-2" />
        </a>
      </div>
    }
  </section>

  <brn-separator hlmSeparator class="flex mx-auto my-20 max-w-2xl" />

  <!-- MCP Servers Section -->
  <section>
    <div class="mb-16 text-center">
      <h2 class="mb-3 text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100 md:text-4xl">MCP Servers</h2>
      <p class="mx-auto max-w-2xl text-gray-600 dark:text-gray-300">
        Explore popular Model Context Protocol (MCP) servers for enhancing AI capabilities.
      </p>
    </div>

    @if (loadingServers) {
      <div class="flex justify-center items-center h-64">
        <app-loading-spinner message="Loading servers..." />
      </div>
    } @else if (serverError) {
      <app-error-message [message]="serverError" (retry)="loadMcpServers()" />
    } @else if (servers.length === 0) {
      <div
        role="status"
        class="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center dark:border-gray-700 dark:bg-gray-800/50"
      >
        <ng-icon hlm [size]="'xl'" [name]="'lucideGlobe'" class="text-gray-400 mb-4" />
        <h2 class="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">No Servers Found</h2>
        <p class="text-gray-600 dark:text-gray-300">There are currently no MCP servers available.</p>
      </div>
    } @else {
      <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        @for (server of servers; track server.id) {
          <a
            [href]="server.url"
            target="_blank"
            rel="noopener noreferrer"
            class="block relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:hover:shadow-gray-700/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
          >
            <div class="p-5 flex flex-col h-full">
              <div class="flex items-center justify-between">
                <h3
                  class="truncate text-lg font-medium leading-tight text-gray-900 dark:text-gray-100"
                  [title]="server.name"
                >
                  {{ server.name }}
                </h3>

                <div class="flex items-center space-x-3">
                  <span
                    hlmBadge
                    variant="secondary"
                    class="font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 pointer-events-none capitalize"
                    [ngClass]="{
                      '!bg-green-100 !text-green-800 dark:!bg-green-900 dark:!text-green-300':
                        server.type === 'official',
                    }"
                  >
                    <ng-icon [name]="server.type === 'official' ? 'lucideBadgeCheck' : 'lucideUsers'" class="mr-1.5" />
                    {{ server.type }}
                  </span>
                </div>
              </div>

              <span class="mb-3 inline-flex items-center text-xs text-gray-500 dark:text-gray-400">
                by&nbsp;
                <span>{{ getRepoOwner(server.url || '') }}</span>
              </span>

              <p class="mb-4 line-clamp-3 text-sm text-gray-600 dark:text-gray-300">
                {{ server.summary || server.description || 'No description available' }}
              </p>

              <div class="flex items-center justify-between mt-auto">
                <div class="flex space-x-4">
                  <div class="flex items-center text-sm" hlmTooltip="GitHub Stars">
                    <ng-icon name="lucideStar" class="mr-1.5 text-yellow-500" />
                    <span class="text-xs text-black dark:text-white">{{ formatNumber(server.stars) || 0 }}</span>
                  </div>
                  <div class="flex items-center text-sm" hlmTooltip="Downloads">
                    <ng-icon name="lucideDownload" class="mr-1.5 text-blue-500" />
                    <span class="text-xs text-black dark:text-white">{{ formatNumber(server.downloads, '--') }}</span>
                  </div>
                </div>
                <button hlmBtn [variant]="'outline'" [size]="'xsm'">
                  Visit Source
                  <ng-icon hlm [size]="'xs'" [name]="'lucideExternalLink'" class="ml-1" />
                </button>
              </div>
            </div>
          </a>
        }
      </div>

      <div class="mt-12 text-center">
        <a hlmBtn routerLink="/mcp-servers" class="inline-flex items-center">
          <span>All MCP Servers</span>
          <ng-icon hlm [size]="'sm'" [name]="'lucideArrowRight'" class="ml-2" />
        </a>
      </div>
    }
  </section>

  <brn-separator hlmSeparator class="flex mx-auto my-20 max-w-2xl" />

  <!-- MCP Clients Section -->
  <section>
    <div class="mb-16 text-center">
      <h2 class="mb-3 text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100 md:text-4xl">MCP Clients</h2>
      <p class="mx-auto max-w-2xl text-gray-600 dark:text-gray-300">
        Discover applications and tools that connect to MCP servers.
      </p>
    </div>

    @if (loadingClients) {
      <div class="flex justify-center items-center h-64">
        <app-loading-spinner message="Loading clients..." />
      </div>
    } @else if (clientError) {
      <app-error-message [message]="clientError" (retry)="loadMcpClients()" />
    } @else if (clients.length === 0) {
      <div
        role="status"
        class="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center dark:border-gray-700 dark:bg-gray-800/50"
      >
        <ng-icon hlm [size]="'xl'" [name]="'lucideLaptop'" class="text-gray-400 mb-4" />
        <h2 class="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">No Clients Found</h2>
        <p class="text-gray-600 dark:text-gray-300">There are currently no MCP clients available.</p>
      </div>
    } @else {
      <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        @for (client of clients; track client.id) {
          <a
            [href]="findGithubUrl(client.urls) || (client.urls && client.urls.length > 0 ? client.urls[0] : '')"
            target="_blank"
            rel="noopener noreferrer"
            class="block relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:hover:shadow-gray-700/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
          >
            <div class="p-5 flex flex-col h-full">
              <div class="flex items-center justify-between space-x-3">
                <h3
                  class="truncate text-lg font-medium leading-tight text-gray-900 dark:text-gray-100"
                  [title]="client.name"
                >
                  {{ client.name }}
                </h3>

                @if (client.isFeatured) {
                  <span
                    hlmBadge
                    variant="secondary"
                    class="font-medium bg-blue-50 text-blue-800 dark:bg-blue-900 dark:text-blue-300 pointer-events-none"
                  >
                    <ng-icon name="lucideBadgeCheck" class="mr-1.5" />
                    Featured
                  </span>
                }
              </div>

              <span class="mb-3 inline-flex items-center text-xs text-gray-500 dark:text-gray-400">
                by&nbsp;
                @if (findGithubUrl(client.urls); as githubUrl) {
                  <span>{{ getRepoOwner(githubUrl) }}</span>
                } @else {
                  <span>{{ getDomainFromUrl(client.urls[0]) }}</span>
                }
              </span>

              <p class="mb-4 line-clamp-3 text-sm text-gray-600 dark:text-gray-300">
                {{ client.summary || 'No summary available.' }}
              </p>

              <div class="flex items-center justify-between mt-auto">
                <div class="flex space-x-4">
                  <div class="flex items-center text-sm" hlmTooltip="GitHub Stars">
                    <ng-icon name="lucideStar" class="mr-1.5 text-yellow-500" />
                    <span class="text-xs text-black dark:text-white">{{ formatNumber(client.stars) }}</span>
                  </div>
                </div>
                <button hlmBtn [variant]="'outline'" [size]="'xsm'">
                  Visit Source
                  <ng-icon hlm [size]="'xs'" [name]="'lucideExternalLink'" class="ml-1" />
                </button>
              </div>
            </div>
          </a>
        }
      </div>

      <div class="mt-12 text-center">
        <a hlmBtn routerLink="/mcp-clients" class="inline-flex items-center">
          <span>All MCP Clients</span>
          <ng-icon hlm [size]="'sm'" [name]="'lucideArrowRight'" class="ml-2" />
        </a>
      </div>
    }
  </section>

  <brn-separator hlmSeparator class="flex mx-auto my-20 max-w-2xl" />

  <!-- Newsletter Subscription Section -->
  <section class="py-12">
    <div class="mx-auto max-w-2xl text-center">
      <h2 class="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100 md:text-4xl">
        Stay Updated with Summerry
      </h2>
      <p class="mt-4 text-lg text-gray-600 dark:text-gray-300">
        Subscribe to our newsletter for the latest AI news, insights, and MCP ecosystem updates.
      </p>
      <div class="mt-8">
        <app-newsletter-subscribe [redirectToConfirmPage]="false" />
      </div>
    </div>
  </section>
</div>
